import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import Modal from '../../components/Modal';
import MeasurementDisplay from '../../components/MeasurementDisplay';
import BatchFilterToggle from '../../components/BatchFilterToggle';
import BatchBadge from '../../components/BatchBadge';

const MedicalAdminAssignCasesToBatch = () => {
  const { user } = useAuth();
  const { batchId } = useParams();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [cases, setCases] = useState([]);
  const [assignedCases, setAssignedCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all'); // 'all', '7days', '30days', '90days'
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [caseToRemove, setCaseToRemove] = useState(null);
  const [casesLoading, setCasesLoading] = useState(false);
  const [totalCases, setTotalCases] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(200);
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');
  const [batchInfo, setBatchInfo] = useState([]);
  
  // New state for other batches cases feature
  const [showOtherBatchesModal, setShowOtherBatchesModal] = useState(false);
  const [otherBatchesCases, setOtherBatchesCases] = useState([]);
  const [otherBatchesCasesLoading, setOtherBatchesCasesLoading] = useState(false);
  const [selectedCasesFromOtherBatches, setSelectedCasesFromOtherBatches] = useState([]);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState('all');
  const [allBatchesList, setAllBatchesList] = useState([]);
  const [selectAllInBatchMode, setSelectAllInBatchMode] = useState(false);
  const [caseFilter, setCaseFilter] = useState('unassigned');  // New: 'assigned' or 'unassigned'
  const [sortBy, setSortBy] = useState('created_at');  // New: sort option
  const perPage = 20;  // Items per page (server-side pagination)
  
  const casesPerPage = 20;  // Display cases per page on frontend

  useEffect(() => {
    if (batchId) {
      loadBatchDetails();
      loadBatchInfo();
      loadAssignedCases();
    }
  }, [batchId]);

  useEffect(() => {
    // Load cases separately after batch details are loaded
    if (batch) {
      loadCases();
    }
  }, [batch]);

  useEffect(() => {
    // Reload cases when filter, page, or sort changes
    if (batch) {
      loadCases();
    }
  }, [caseFilter, currentPage, searchTerm, sortBy]);

  const loadBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/batches/${batchId}`);
      setBatch(response.data.batch);
    } catch (err) {
      console.error('Error loading batch:', err);
      setError('Failed to load batch details: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadCases = async (limit = 200, daysBack = null) => {
    try {
      setCasesLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        filter: caseFilter,  // 'assigned' or 'unassigned'
        page: currentPage,
        per_page: perPage,
        sort_by: sortBy
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/cases/batch-filter?${params}`);
      const casesData = response.data.cases || [];
      setCases(casesData);
      setTotalCases(response.data.total || 0);
      
    } catch (err) {
      console.error('Error loading cases:', err);
      setError('Failed to load cases: ' + (err.response?.data?.message || err.message));
      setCases([]);
      setTotalCases(0);
    } finally {
      setCasesLoading(false);
    }
  };

  const loadMoreCases = async () => {
    const newLimit = currentLimit + 200;
    await loadCases(newLimit);
  };

  const loadBatchInfo = async () => {
    try {
      const response = await api.get('/batches?with_case_counts=true');
      setBatchInfo(response.data.batches || []);
    } catch (err) {
      console.error('Error loading batch info:', err);
      setBatchInfo([]);
    }
  };

  const loadAssignedCases = async () => {
    try {
      const response = await api.get(`/batches/${batchId}`);
      const batchCases = response.data.cases || [];
      setAssignedCases(batchCases);
    } catch (err) {
      console.error('Error loading assigned cases:', err);
    }
  };

  const handleCaseToggle = (caseId) => {
    setSelectedCases(prev => {
      if (prev.includes(caseId)) {
        return prev.filter(id => id !== caseId);
      } else {
        return [...prev, caseId];
      }
    });
  };

  const handleSelectAll = () => {
    const visibleCaseIds = paginatedCases.map(c => c.id);
    const allSelected = visibleCaseIds.every(id => selectedCases.includes(id));
    
    if (allSelected) {
      setSelectedCases(prev => prev.filter(id => !visibleCaseIds.includes(id)));
    } else {
      setSelectedCases(prev => [...prev, ...visibleCaseIds.filter(id => !prev.includes(id))]);
    }
  };

  const handleAssignCases = async () => {
    if (selectedCases.length === 0) {
      setError('Please select at least one case to assign');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await api.post(`/batches/${batchId}/cases`, {
        case_ids: selectedCases
      });

      const { assigned_cases, skipped_cases } = response.data;

      // Build success message
      let message = `Successfully assigned ${assigned_cases.length} case(s) to batch.`;
      
      // Handle skipped cases with detailed reasons
      if (skipped_cases && skipped_cases.length > 0) {
        const skippedDetails = skipped_cases
          .map(skip => {
            if (typeof skip === 'object') {
              return `Case #${skip.case_id} (already in Batch: ${skip.batch_name})`;
            }
            return `Case #${skip}`;
          })
          .join(', ');
        
        message += `\n\nCannot reassign ${skipped_cases.length} case(s): These cases are already assigned to batches and cannot be reassigned between batches.\n${skippedDetails}`;
        
        // Show warning for partial assignment
        if (assigned_cases.length > 0) {
          setSuccess(message);
        } else {
          // All cases were skipped
          setError(message);
        }
      } else {
        setSuccess(message);
      }

      setSelectedCases([]);
      await loadAssignedCases();
      await loadCases();  // Refresh case list to update batch badges

      // Clear success message after timeout
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, skipped_cases && skipped_cases.length > 0 ? 8000 : 5000);

    } catch (err) {
      console.error('Error assigning cases:', err);
      setError(err.response?.data?.error || 'Failed to assign cases to batch');
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleRemoveCase = (caseId) => {
    const batchCase = assignedCases.find(bc => bc.case_id === caseId);
    setCaseToRemove(batchCase);
    setShowRemoveModal(true);
  };

  const confirmRemoveCase = async () => {
    if (!caseToRemove) return;

    try {
      setLoading(true);
      await api.delete(`/batches/${batchId}/cases/${caseToRemove.case_id}`);
      
      setSuccess('Case removed from batch successfully');
      await loadAssignedCases();
      
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error removing case:', err);
      setError(err.response?.data?.error || 'Failed to remove case from batch');
    } finally {
      setLoading(false);
      setShowRemoveModal(false);
      setCaseToRemove(null);
    }
  };

  const isCaseAssigned = (caseId) => {
    return assignedCases.some(batchCase => batchCase.case_id === caseId);
  };

  const isCaseSelected = (caseId) => {
    return selectedCases.includes(caseId);
  };

  // Check if case is in batch with specific status
  const isCaseInBatchStatus = (caseId, status) => {
    if (status === 'all') return true;
    
    // Get all cases assigned to batches with specific status
    let casesInStatus = [];
    
    if (status === 'active') {
      casesInStatus = assignedCases.filter(batchCase => batchCase.batch?.is_active).map(bc => bc.case_id);
    } else if (status === 'scheduled') {
      casesInStatus = assignedCases.filter(batchCase => !batchCase.batch?.is_active && !batchCase.batch?.is_expired).map(bc => bc.case_id);
    } else if (status === 'expired') {
      casesInStatus = assignedCases.filter(batchCase => batchCase.batch?.is_expired).map(bc => bc.case_id);
    }
    
    if (status === 'no_batch') {
      // Case has no batch assignments
      return assignedCases.filter(bc => bc.case_id === caseId).length === 0;
    }
    
    // Check if case is in any batch with the specified status
    return casesInStatus.includes(caseId);
  };

  const getHealthColorClass = (prediction) => {
    switch (prediction?.toLowerCase()) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-info';
      case 'fair': return 'text-warning';
      case 'at risk':
      case 'needs attention':
      case 'high risk':
      case 'critical': return 'text-danger';
      default: return 'text-muted';
    }
  };

  const getColorName = (prediction) => {
    if (!prediction) return 'N/A';
    const colorMap = {
      "Excellent": "Excellent",
      "Good": "Good",
      "Fair": "Fair",
      "High Risk": "High Risk",
      "Critical": "Critical"
    };
    if (typeof prediction === "string" && prediction.startsWith("#")) {
      const hexMap = {
        "#83D65C": "Excellent",
        "#5BC0DE": "Good",
        "#ffe366": "Good",
        "#ff944d": "Fair",
        "#ff6666": "High Risk",
        "#d9534f": "Critical"
      };
      return hexMap[prediction] || prediction;
    }
    return colorMap[prediction] || prediction;
  };

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-danger';
  };

  const filteredCases = cases.filter(case_ => {
    const matchesSearch =
      String(case_.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (case_.physicalHealthPrediction || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (case_.mentalHealthPrediction || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Apply assignment filter
    let assignmentMatch = false;
    if (assignmentFilter === 'all') {
      assignmentMatch = true;
    } else if (assignmentFilter === 'assigned') {
      assignmentMatch = isCaseAssigned(case_.id);
    } else if (assignmentFilter === 'unassigned') {
      assignmentMatch = !isCaseAssigned(case_.id);
    }
    
    // Apply batch status filter
    const batchMatch = isCaseInBatchStatus(case_.id, batchStatusFilter);
    
    return matchesSearch && assignmentMatch && batchMatch;
  });

  // Use API's total count for pagination (server-side pagination)
  const totalPages = Math.ceil(totalCases / perPage);
  // No need to slice - cases are already paginated from API
  const paginatedCases = cases;

  const allVisibleSelected = paginatedCases.length > 0 && paginatedCases.every(c => isCaseSelected(c.id));

  const getVisiblePageNumbers = () => {
    const pages = [];
    const pagesToShowAround = 2; // Show 2 pages before and after current
    const startPage = Math.max(1, currentPage - pagesToShowAround);
    const endPage = Math.min(totalPages, currentPage + pagesToShowAround);
    
    // Always add first page
    if (startPage > 1) {
      pages.push(1);
    }
    
    // Add ellipsis if there's a gap
    if (startPage > 2) {
      pages.push('...');
    }
    
    // Add pages around current
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if there's a gap
    if (endPage < totalPages - 1) {
      pages.push('...');
    }
    
    // Always add last page if not already included
    if (endPage < totalPages) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  const getBatchStatus = () => {
    if (!batch) return { text: 'Unknown', class: 'bg-secondary' };
    
    if (batch.is_expired) {
      return { text: 'Expired', class: 'bg-danger' };
    } else if (batch.is_active) {
      return { text: 'Active', class: 'bg-success' };
    } else {
      return { text: 'Scheduled', class: 'bg-warning' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // NEW: Functions for viewing and reassigning cases from other batches
  const loadCasesFromOtherBatches = async () => {
    try {
      setOtherBatchesCasesLoading(true);
      const response = await api.get(`/batches/other-batches-cases?exclude_batch_id=${batchId}`);
      setOtherBatchesCases(response.data.cases || []);
      setAllBatchesList(response.data.batches || []);
    } catch (err) {
      console.error('Error loading cases from other batches:', err);
      setError('Failed to load cases from other batches: ' + (err.response?.data?.error || err.message));
      setOtherBatchesCases([]);
      setAllBatchesList([]);
    } finally {
      setOtherBatchesCasesLoading(false);
    }
  };

  const handleOpenOtherBatchesModal = () => {
    setShowOtherBatchesModal(true);
    loadCasesFromOtherBatches();
  };

  const handleToggleCaseFromOtherBatch = (caseId, sourceBatchId) => {
    setSelectedCasesFromOtherBatches(prev => {
      // Check if already selected
      const existingIndex = prev.findIndex(item => item.caseId === caseId);
      if (existingIndex >= 0) {
        // Remove if exists
        return prev.filter(item => item.caseId !== caseId);
      } else {
        // Add new selection
        return [...prev, { caseId, sourceBatchId }];
      }
    });
  };

  const handleSelectAllInFilteredBatch = () => {
    // Get cases currently displayed (filtered by batch filter)
    const filteredCases = selectedBatchFilter === 'all'
      ? otherBatchesCases
      : otherBatchesCases.filter(item => item.batch_id === parseInt(selectedBatchFilter));
    
    // Check if all are already selected
    const allSelected = filteredCases.every(item => 
      selectedCasesFromOtherBatches.some(sel => sel.caseId === item.case_id)
    );
    
    if (allSelected) {
      // Deselect all in filtered batch
      const filteredCaseIds = filteredCases.map(item => item.case_id);
      setSelectedCasesFromOtherBatches(prev => 
        prev.filter(item => !filteredCaseIds.includes(item.caseId))
      );
    } else {
      // Select all in filtered batch
      const newSelections = filteredCases
        .filter(item => !selectedCasesFromOtherBatches.some(sel => sel.caseId === item.case_id))
        .map(item => ({ caseId: item.case_id, sourceBatchId: item.batch_id }));
      setSelectedCasesFromOtherBatches(prev => [...prev, ...newSelections]);
    }
  };

  const isAllFilteredSelected = () => {
    const filteredCases = selectedBatchFilter === 'all'
      ? otherBatchesCases
      : otherBatchesCases.filter(item => item.batch_id === parseInt(selectedBatchFilter));
    
    if (filteredCases.length === 0) return false;
    
    return filteredCases.every(item => 
      selectedCasesFromOtherBatches.some(sel => sel.caseId === item.case_id)
    );
  };

  const isCaseFromOtherBatchSelected = (caseId) => {
    return selectedCasesFromOtherBatches.some(item => item.caseId === caseId);
  };

  const handleReassignCases = async () => {
    if (selectedCasesFromOtherBatches.length === 0) {
      setError('Please select at least one case to reassign');
      return;
    }

    try {
      setReassignLoading(true);
      setError('');

      // Group cases by source batch for the API call
      const casesByBatch = {};
      selectedCasesFromOtherBatches.forEach(item => {
        if (!casesByBatch[item.sourceBatchId]) {
          casesByBatch[item.sourceBatchId] = [];
        }
        casesByBatch[item.sourceBatchId].push(item.caseId);
      });

      // Reassign cases batch by batch
      let totalReassigned = 0;
      for (const [sourceBatchId, caseIds] of Object.entries(casesByBatch)) {
        const response = await api.post('/batches/reassign-cases', {
          case_ids: caseIds,
          source_batch_id: parseInt(sourceBatchId),
          target_batch_id: parseInt(batchId)
        });
        totalReassigned += response.data.reassigned_count || 0;
      }

      setSuccess(`Successfully reassigned ${totalReassigned} case(s) to this batch`);
      setSelectedCasesFromOtherBatches([]);
      await loadCasesFromOtherBatches(); // Refresh the list
      await loadAssignedCases(); // Refresh assigned cases

      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      console.error('Error reassigning cases:', err);
      setError(err.response?.data?.error || 'Failed to reassign cases');
    } finally {
      setReassignLoading(false);
    }
  };

  if (!batch) {
    return (
      <ResearchLayout pageTitle="Assign Cases to Batch">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading batch details...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (casesLoading && cases.length === 0) {
    return (
      <ResearchLayout pageTitle={`Assign Cases to Batch - ${batch.name}`}>
        <div className="container-fluid">
          {/* Show batch info while cases are loading */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-1">
                    <i className="fas fa-layer-group me-2"></i>
                    {batch.name}
                  </h4>
                  <small className="opacity-75">
                    {batch.description || 'No description provided'}
                  </small>
                </div>
                <div className="card-body">
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading cases assigned to you...</p>
                    <small className="text-muted">This may take a moment for large datasets</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  const status = getBatchStatus();

  return (
    <ResearchLayout pageTitle={`Assign Cases to Batch - ${batch.name}`}>
      <div className="container-fluid">
        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Batch Information Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <div className="row align-items-center">
                  <div className="col">
                    <h4 className="mb-1">
                      <i className="fas fa-layer-group me-2"></i>
                      {batch.name}
                    </h4>
                    <small className="opacity-75">
                      {batch.description || 'No description provided'}
                    </small>
                  </div>
                  <div className="col-auto">
                    <span className={`badge ${status.class} fs-6`}>
                      {status.text}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">Start Date</div>
                    <div className="fw-bold">
                      <i className="fas fa-play me-1 text-success"></i>
                      {formatDate(batch.start_date)}
                    </div>
                  </div>
                  
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">End Date</div>
                    <div className="fw-bold">
                      <i className="fas fa-stop me-1 text-danger"></i>
                      {formatDate(batch.extended_end_date || batch.end_date)}
                      {batch.extended_end_date && (
                        <small className="d-block text-success">
                          <i className="fas fa-clock"></i> Extended
                        </small>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">Time Remaining</div>
                    <div className={`fw-bold ${batch.is_expired ? 'text-danger' : batch.is_active ? 'text-success' : 'text-warning'}`}>
                      <i className="fas fa-hourglass-half me-1"></i>
                      {batch.time_remaining || 'Expired'}
                    </div>
                  </div>
                  
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">Grace Period</div>
                    <div className="fw-bold">
                      <i className="fas fa-shield-alt me-1 text-info"></i>
                      {batch.grace_period_hours}h
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="small text-muted">Cases Currently in Batch</div>
                    <div className="h5 mb-0 text-primary">
                      <i className="fas fa-file-medical me-1"></i>
                      {assignedCases.length} Cases
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="small text-muted">Available Cases to Assign</div>
                    <div className="h5 mb-0 text-success">
                      <i className="fas fa-plus-circle me-1"></i>
                      {filteredCases.length} Cases
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <button
                    className="btn btn-outline-info"
                    onClick={handleOpenOtherBatchesModal}
                  >
                    <i className="fas fa-exchange-alt me-1"></i>
                    View & Reassign Cases from Other Batches
                  </button>
                </div>

                <div className="mt-3">
                  <div className="small text-muted mb-2">
                    <i className="fas fa-info-circle me-1"></i>
                    Showing {cases.length} of {totalCases} total cases assigned to you
                    {totalCases > cases.length && (
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={loadMoreCases}
                        disabled={casesLoading}
                      >
                        {casesLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="fas fa-plus me-1"></i>
                        )}
                        Load More Cases
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Assignment Controls */}
          <div className="col-12 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <i className="fas fa-file-medical text-primary me-2"></i>
                    Assign Cases to Batch
                  </h5>
                  <small className="text-muted">
                    Select cases to add to this batch
                  </small>
                </div>
                
                {selectedCases.length > 0 && (
                  <button 
                    className="btn btn-success"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={loading}
                  >
                    <i className="fas fa-plus me-1"></i>
                    Assign {selectedCases.length} Case{selectedCases.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
              
              <div className="card-body">
                {/* Batch Filter Toggle */}
                <div className="mb-4">
                  <BatchFilterToggle
                    currentFilter={caseFilter}
                    onFilterChange={(filter) => {
                      setCaseFilter(filter);
                      setCurrentPage(1);
                      setSelectedCases([]);
                    }}
                    loading={casesLoading}
                    counts={{
                      assigned: totalCases && caseFilter === 'assigned' ? totalCases : 0,
                      unassigned: totalCases && caseFilter === 'unassigned' ? totalCases : 0
                    }}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by customer ID..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="created_at">Sort by Creation Date</option>
                      <option value="assigned_at">Sort by Assignment Date</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <span className="form-control-plaintext text-muted">
                      {casesLoading ? 'Loading...' : `${cases.length} of ${totalCases} cases (Page ${currentPage}/${totalPages})`}
                    </span>
                  </div>
                </div>

                {/* Second row of filters - Date Range */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={dateRangeFilter}
                      onChange={async (e) => {
                        const value = e.target.value;
                        setDateRangeFilter(value);
                        setCurrentPage(1);
                        
                        // Map filter to days back
                        const daysMap = {
                          'all': null,
                          '7days': 7,
                          '30days': 30,
                          '90days': 90
                        };
                        
                        // Reload cases with the date filter
                        await loadCases(currentLimit, daysMap[value]);
                      }}
                    >
                      <option value="all">All Time</option>
                      <option value="7days">Last 7 Days</option>
                      <option value="30days">Last 30 Days</option>
                      <option value="90days">Last 90 Days</option>
                    </select>
                  </div>
                  <div className="col-md-2 text-end">
                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={handleSelectAll}
                    >
                      <i className="fas fa-check-square me-1"></i>
                      {allVisibleSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                </div>
                
                {batchStatusFilter !== 'all' && (
                  <div className="alert alert-info">
                    <i className="fas fa-filter me-2"></i>
                    <strong>Batch Status Filter:</strong>
                    Showing cases available for {batchStatusFilter} batches
                    {batch.is_expired && (
                      <small className="d-block mt-1">
                        <i className="fas fa-exclamation-triangle"></i>
                        Current batch is expired - consider extending or selecting active batches
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Available Cases Grid */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h6 className="mb-0">
                  <i className="fas fa-list text-info me-2"></i>
                  Available Cases ({filteredCases.length})
                </h6>
              </div>
              
              <div className="card-body">
                {paginatedCases.length > 0 ? (
                  <div className="row">
                    {paginatedCases.map((case_) => (
                      <div key={case_.id} className="col-md-6 mb-3">
                        <div className={`card h-100 border-2 ${isCaseSelected(case_.id) ? 'border-primary bg-light' : isCaseAssigned(case_.id) ? 'border-success bg-light' : 'border'}`}>
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isCaseSelected(case_.id)}
                                  onChange={() => handleCaseToggle(case_.id)}
                                  id={`case-${case_.id}`}
                                  disabled={isCaseAssigned(case_.id)}
                                />
                                <label className="form-check-label fw-bold ms-2" htmlFor={`case-${case_.id}`}>
                                  Case #{case_.id}
                                </label>
                                {isCaseAssigned(case_.id) && (
                                  <span className="badge bg-success ms-2">In Batch</span>
                                )}
                              </div>
                              <span className={`badge ${getScoreBadgeClass(case_.score)}`}>
                                {case_.score}
                              </span>
                            </div>

                            {/* Batch Badge Display */}
                            <div className="mb-2">
                              <BatchBadge
                                batches={case_.batch_assignments || []}
                                isUnassigned={!case_.batch_assignments || case_.batch_assignments.length === 0}
                              />
                            </div>
                            
                            {/* Demographics */}
                            <div className="row mb-2">
                              <div className="col-6">
                                <small className="text-muted">Age</small>
                                <div className="fw-bold small">{case_.age || 'N/A'}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Gender</small>
                                <div className="fw-bold small">{case_.gender || 'N/A'}</div>
                              </div>
                            </div>

                            {/* Health Predictions */}
                            <div className="row mb-2">
                              <div className="col-6">
                                <small className="text-muted">Physical</small>
                                <div className={`fw-bold small ${getHealthColorClass(case_.physicalHealthPrediction)}`}>
                                  {getColorName(case_.physicalHealthPrediction)}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Mental</small>
                                <div className={`fw-bold small ${getHealthColorClass(case_.mentalHealthPrediction)}`}>
                                  {getColorName(case_.mentalHealthPrediction)}
                                </div>
                              </div>
                            </div>

                            {/* Basic Info */}
                            <div className="row mb-2">
                              <div className="col-4">
                                <small className="text-muted">Weight</small>
                                <div className="fw-bold small">
                                  <MeasurementDisplay
                                    measurementType="weight"
                                    value={case_.weight}
                                    originalUnit={case_.weightUnit}
                                    showLabel={false}
                                  />
                                </div>
                              </div>
                              <div className="col-4">
                                <small className="text-muted">BMI</small>
                                <div className="fw-bold small">
                                  {case_.weight && case_.height ?
                                    (case_.weight / Math.pow(case_.height / 100, 2)).toFixed(1) : 'N/A'}
                                </div>
                              </div>
                              <div className="col-4">
                                <small className="text-muted">BP</small>
                                <div className="fw-bold small">
                                  {case_.systolicBloodPressure}/{case_.diastolicBloodPressure}
                                </div>
                              </div>
                            </div>

                            {/* Completion */}
                            <div className="small text-muted">
                              <i className="fas fa-chart-bar me-1"></i>
                              Completion: {case_.assessmentCompletionPercentage}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-file-medical fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">
                      {searchTerm ? 'No cases match your search' : 'No available cases'}
                    </h6>
                    <p className="text-muted">
                      {searchTerm 
                        ? 'Try adjusting your search criteria.'
                        : 'All available cases have been assigned to this batch.'
                      }
                    </p>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4">
                    <nav aria-label="Cases pagination">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        
                        {getVisiblePageNumbers().map((page, index) => {
                          if (page === '...') {
                            return (
                              <li key={`ellipsis-${index}`} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          return (
                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        })}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Currently Assigned Cases */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h6 className="mb-0">
                  <i className="fas fa-folder text-info me-2"></i>
                  Currently in Batch ({assignedCases.length})
                </h6>
              </div>
              
              <div className="card-body">
                {assignedCases.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {assignedCases.map((batchCase) => (
                      <div key={batchCase.id} className="list-group-item px-0 border-0">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="fw-bold">Case #{batchCase.case_id}</div>
                            <div className="small text-muted">
                              Assigned: {new Date(batchCase.assigned_at).toLocaleDateString()}
                            </div>
                            {batchCase.case && (
                              <div className="small">
                                <span className="badge bg-success">
                                  {batchCase.case.assessmentCompletionPercentage}% Complete
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="ms-2">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveCase(batchCase.case_id)}
                              disabled={loading}
                              title="Remove from batch"
                            >
                              <i className="fas fa-minus-circle"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Cases in Batch</h6>
                    <p className="text-muted small">
                      Assign cases to this batch to make them available to doctors.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Confirm Case Assignment"
          confirmText="Assign Cases"
          onConfirm={handleAssignCases}
          isLoading={loading}
        >
          <p>Are you sure you want to assign the following {selectedCases.length} case{selectedCases.length !== 1 ? 's' : ''} to this batch?</p>
          
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Note:</strong> These cases will be available to doctors assigned to this batch according to the batch schedule and deadlines.
          </div>
        </Modal>

        {/* Remove Case Confirmation Modal */}
        <Modal
          isOpen={showRemoveModal}
          onClose={() => setShowRemoveModal(false)}
          title="Remove Case from Batch"
          confirmText="Remove Case"
          onConfirm={confirmRemoveCase}
          isLoading={loading}
        >
          <p>Are you sure you want to remove <strong>Case #{caseToRemove?.case_id}</strong> from this batch?</p>
          
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action will remove the case from the batch. All doctors will lose access to this case unless reassigned.
          </div>
        </Modal>

        {/* View & Reassign Cases from Other Batches Modal */}
        <Modal
          isOpen={showOtherBatchesModal}
          onClose={() => {
            setShowOtherBatchesModal(false);
            setSelectedCasesFromOtherBatches([]);
            setSelectedBatchFilter('all');
          }}
          title="View & Reassign Cases from Other Batches"
          size="lg"
        >
          <div className="mb-3">
            <p className="text-muted">
              These cases are currently assigned to other batches. You can view their details and reassign them to this batch.
            </p>
          </div>

          {/* Batch Filter */}
          {allBatchesList.length > 0 && (
            <div className="mb-3">
              <label className="form-label fw-bold">Filter by Batch:</label>
              <select
                className="form-select"
                value={selectedBatchFilter}
                onChange={(e) => setSelectedBatchFilter(e.target.value)}
              >
                <option value="all">All Batches ({otherBatchesCases.length} cases)</option>
                {allBatchesList.map((batch) => {
                  const count = otherBatchesCases.filter(c => c.batch_id === batch.id).length;
                  return (
                    <option key={batch.id} value={batch.id}>
                      {batch.name} ({count} cases)
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {otherBatchesCasesLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading cases from other batches...</p>
            </div>
          ) : otherBatchesCases.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
              <h6 className="text-muted">No Cases in Other Batches</h6>
              <p className="text-muted small">
                There are no cases assigned to other batches you created.
              </p>
            </div>
          ) : (
            <>
              {(() => {
                // Filter cases by selected batch
                const filteredCases = selectedBatchFilter === 'all'
                  ? otherBatchesCases
                  : otherBatchesCases.filter(item => item.batch_id === parseInt(selectedBatchFilter));
                
                return filteredCases.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-filter fa-2x text-muted mb-3"></i>
                    <p className="text-muted">No cases found in the selected batch.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table table-hover table-sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th style={{ width: '40px' }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isAllFilteredSelected()}
                                onChange={handleSelectAllInFilteredBatch}
                                title={isAllFilteredSelected() ? "Deselect all visible" : "Select all visible"}
                              />
                            </th>
                            <th>Case ID</th>
                            <th>Source Batch</th>
                            <th>Batch Status</th>
                            <th>Age</th>
                            <th>Gender</th>
                            <th>Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCases.map((item) => {
                            const caseData = item.case_data || {};
                            return (
                              <tr key={`${item.batch_id}-${item.case_id}`}>
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isCaseFromOtherBatchSelected(item.case_id)}
                                    onChange={() => handleToggleCaseFromOtherBatch(item.case_id, item.batch_id)}
                                  />
                                </td>
                                <td>
                                  <strong>Case #{item.case_id}</strong>
                                </td>
                                <td>
                                  <span className="badge bg-secondary">
                                    {item.batch_name}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    item.batch_status === 'active' ? 'bg-success' :
                                    item.batch_status === 'completed' ? 'bg-info' :
                                    item.batch_status === 'cancelled' ? 'bg-danger' : 'bg-warning'
                                  }`}>
                                    {item.batch_status}
                                  </span>
                                </td>
                                <td>{caseData.age || 'N/A'}</td>
                                <td>{caseData.gender || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${caseData.score >= 80 ? 'bg-success' : caseData.score >= 60 ? 'bg-warning' : 'bg-danger'}`}>
                                    {caseData.score || 'N/A'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {selectedCasesFromOtherBatches.length > 0 && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="d-flex justify-content-between align-items-center">
                          <span>
                            <strong>{selectedCasesFromOtherBatches.length}</strong> case(s) selected for reassignment
                          </span>
                          <button
                            className="btn btn-primary"
                            onClick={handleReassignCases}
                            disabled={reassignLoading}
                          >
                            {reassignLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Reassigning...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-exchange-alt me-2"></i>
                                Reassign to This Batch
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminAssignCasesToBatch;